
import React, { useState, useEffect } from 'react';
import {axiosInstance, localAxiosGet, localAxiosToken, localAxios, localAxiosPut } from '../../../../Axios/axios';
import axios from 'axios';
import { SnackbarProvider, useSnackbar } from 'notistack';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, InputAdornment, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, FormControl, InputLabel, Select, MenuItem, LinearProgress,
  IconButton, Tooltip, Card, CardContent, Divider, Avatar, Chip, Stack, useTheme, alpha,
  TablePagination
} from '@mui/material';
import {
  Search as SearchIcon, Refresh as RefreshIcon, Edit as EditIcon,
  History as HistoryIcon, Warning as WarningIcon, Close as CloseIcon,
  Add as AddIcon, Save as SaveIcon, EventNote as EventNoteIcon,
  CheckCircle as CheckCircleIcon, Error as ErrorIcon
} from '@mui/icons-material';

const leaveTypeColors = {
  'Sick Leave': '#f44336',
  'Casual Leave': '#2196f3',
  'Earned Leave': '#4caf50',
  'Maternity Leave': '#9c27b0',
  'Paternity Leave': '#673ab7',
};

const leaveTypes = ['Earned Leave', 'Sick Leave', 'Casual Leave', 'Maternity Leave', 'Paternity Leave'];


const LeaveBalancesWithSnackbar = () => {
  return (

    <LeaveBalances />

  );
};

const LeaveBalances = () => {
  const theme = useTheme();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [employees, setEmployees] = useState([]);
  const [organizationId, setOrganizationId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('All');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [currentLeaveType, setCurrentLeaveType] = useState('');
  const [currentLeaveBalance, setCurrentLeaveBalance] = useState({
    total: 0,
    used: 0,
    pending: 0,
    remaining: 0
  });
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState(['All']);
  const [error, setError] = useState(null);
  const [organizationLeaveTypes, setOrganizationLeaveTypes] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);


  // Add this state to manage selected leave types for each employee
  const [selectedLeaveTypes, setSelectedLeaveTypes] = useState({});

  // Function to handle leave type selection for each employee
  const handleLeaveTypeSelect = (employeeId, leaveType) => {
    setSelectedLeaveTypes(prev => ({
      ...prev,
      [employeeId]: leaveType
    }));
  };
  const getSelectedLeaveBalance = (employee) => {
    const selectedType = selectedLeaveTypes[employee.id];
    return employee.leaveBalances.find(balance => balance.type === selectedType) ||
      employee.leaveBalances[0] ||
      { type: 'No Leave', total: 0, used: 0, pending: 0, remaining: 0 };
  };

  const showNotification = (message, variant) => {
    const icon = variant === 'success'
      ? <CheckCircleIcon style={{ marginRight: '8px', color: theme.palette.success.main }} />
      : <ErrorIcon style={{ marginRight: '8px', color: theme.palette.error.main }} />;

    enqueueSnackbar(
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {icon}
        <span>{message}</span>
      </div>,
      {
        variant: variant,
        preventDuplicate: true,
        action: (key) => (
          <IconButton size="small" onClick={() => closeSnackbar(key)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )
      }
    );
  };

  const fetchLeaveTypes = async (employeeUserId) => {
    try {
      const token = localStorage.getItem('accessToken');
      const userId = employeeUserId || localStorage.getItem('userId');

      if (!userId) {
        console.error('User ID not found');
        showNotification('User ID not found. Please try again.', 'error');
        return;
      }

      const config = localAxiosGet(`organization/getAllLeaveTypesByEmpId?user_id=${userId}`, token);
      const response = await axiosInstance(config);

      if (response.data.status === 'SUCCESS') {
        setOrganizationLeaveTypes(response.data.message.leaveTypesWithBalance);
      } else {
        console.error('Failed to fetch leave types');
        showNotification('Failed to fetch leave types', 'error');
      }
    } catch (err) {
      console.error('Error fetching leave types:', err);
      showNotification('Error fetching leave types: ' + (err.message || 'Unknown error'), 'error');
    }
  };

  const fetchLeaveBalances = async (orgId) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken');
      const config = localAxiosGet(`organization/getAllLeaveBalancesByOrgId?organization_id=${orgId || organizationId}`, token);
      const response = await axiosInstance(config);
      if (response.data.status === 'SUCCESS') {
        const transformedData = transformApiData(response.data.result);
        setEmployees(transformedData);
        const uniqueDepartments = ['All', ...new Set(transformedData.map(emp => emp.department))];
        setDepartments(uniqueDepartments);
        showNotification('Leave balances loaded successfully', 'success');
      } else {
        setError('Failed to fetch leave balances');
        showNotification('Failed to fetch leave balances', 'error');
      }
    } catch (err) {
      console.error('Error fetching leave balances:', err);
      setError('Error connecting to server');
      showNotification('Error connecting to server', 'error');
    } finally {
      setLoading(false);
    }
  };

  const transformApiData = (apiData) => {
    return apiData.map(employee => {
      const nameParts = employee.employee_name.split(' ');
      const avatar = nameParts
      .slice(0, 2)
      .map(part => part[0]?.toUpperCase() || '')
      .join('');
      
      const leaveBalances = employee.leave_types.map(leaveType => {
        if (!leaveType.leave_type) {
          return {
            type: 'No Leave Assigned',
            total: 0,
            used: 0,
            pending: 0,
            remaining: 0,
            leave_type_id: null
          };
        }

        return {
          type: leaveType.leave_type,
          total: leaveType.total,
          used: leaveType.used_days,
          pending: 0,
          remaining: leaveType.remaining_days,
          leave_type_id: leaveType.leave_type_id
        };
      });

      const filteredLeaveBalances = leaveBalances.filter(lb => lb.type !== 'No Leave Assigned' || leaveBalances.length === 1);

      return {
        id: employee.user_id,
        name: employee.employee_name,
        // employeeId: `EMP${employee.user_id}`,
        employeeId:employee.emp_id,
        department: employee.department,
        avatar: avatar,
        leaveBalances: filteredLeaveBalances
      };
    });
  };
  useEffect(() => {
    const storedOrgId = localStorage.getItem('organizationId');
    if (storedOrgId) {
      setOrganizationId(storedOrgId);
    }

    if (storedOrgId) {
      fetchLeaveBalances(storedOrgId);
      fetchLeaveTypes();
    }
  }, []);

  const handleLeaveTypeChange = (e) => {
    const selectedType = e.target.value;

    const leaveBalance = currentEmployee.leaveBalances.find(lb => lb.type === selectedType);
    if (leaveBalance) {
      setCurrentLeaveType(selectedType);
      setCurrentLeaveBalance(leaveBalance);
    } else {
      const orgLeaveType = organizationLeaveTypes.find(lt => lt.leave_type === selectedType);
      if (orgLeaveType) {
        setCurrentLeaveType(selectedType);
        setCurrentLeaveBalance({
          type: selectedType,
          leave_type_id: orgLeaveType.id,
          total: orgLeaveType.max_days || 0,
          used: 0,
          pending: 0,
          remaining: orgLeaveType.max_days || 0
        });
      } else {
        setCurrentLeaveType(selectedType);
        setCurrentLeaveBalance({
          type: selectedType,
          total: 0,
          used: 0,
          pending: 0,
          remaining: 0
        });
      }
    }
  };

  const filteredEmployees = employees.filter(employee => {
    const isNameMatch = employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.employeeId.toLowerCase().includes(searchTerm.toLowerCase());
    const isDepartmentMatch = selectedDepartment === 'All' || employee.department === selectedDepartment;
    return isNameMatch && isDepartmentMatch;
  });

  const flattenedRows = filteredEmployees.flatMap(employee =>
    employee.leaveBalances.map((balance, index) => ({
      employee,
      balance,
      isFirstRow: index === 0,
      rowSpan: employee.leaveBalances.length
    }))
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedRows = rowsPerPage > 0
    ? flattenedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : flattenedRows;

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const handleDepartmentChange = (e) => {
    setSelectedDepartment(e.target.value);
    setPage(0);
  };

  const handleOpenDialog = (employee) => {
    setCurrentEmployee(employee);
    setCurrentLeaveType(employee.leaveBalances[0]?.type || '');
    setCurrentLeaveBalance(employee.leaveBalances[0] || {
      total: 0,
      used: 0,
      pending: 0,
      remaining: 0
    });


    fetchLeaveTypes(employee.id);

    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleBalanceChange = (e) => {
    const { name, value } = e.target;
    const numValue = parseFloat(value) || 0;
    let newBalance = { ...currentLeaveBalance, [name]: numValue };
    if (name === 'total' || name === 'used' || name === 'pending') {
      newBalance.remaining = newBalance.total - newBalance.used - newBalance.pending;
    }
    setCurrentLeaveBalance(newBalance);
  };

  const handleSaveBalance = async () => {
    try {
      const token = localStorage.getItem('accessToken');

      if (currentLeaveType === 'No Leave Assigned') {
        setError('Please select a valid leave type before saving');
        showNotification('Please select a valid leave type before saving', 'error');
        return;
      }

      const selectedLeaveBalance = currentEmployee.leaveBalances.find(lb => lb.type === currentLeaveType);

      if (selectedLeaveBalance && selectedLeaveBalance.leave_type_id) {
        const payload = {
          emp_leave_balance_id: selectedLeaveBalance.leave_type_id,
          max_days: currentLeaveBalance.total,
          balance_days: currentLeaveBalance.remaining,
          user_id: currentEmployee.id
        };

        const config = localAxiosPut('/organization/updateLeaveBalance', payload, token);

        const response = await axiosInstance(config);

        if (response.data.status === 'SUCCESS') {
          updateEmployeeLeaveBalance(selectedLeaveBalance.leave_type_id);

          showNotification('Leave balance updated successfully', 'success');

          handleCloseDialog();
        } else {
          const errorMsg = 'Failed to update leave balance: ' + (response.data.message || 'Unknown error');
          setError(errorMsg);
          showNotification(errorMsg, 'error');
          console.error('Failed to update leave balance:', response.data);
        }
      }
      else {
        const orgLeaveType = organizationLeaveTypes.find(lt => lt.leave_type === currentLeaveType);

        if (orgLeaveType) {
          const errorMsg = 'Please use an existing leave type with a valid leave_type_id';
          setError(errorMsg);
          showNotification(errorMsg, 'error');
        } else {
          const errorMsg = 'Selected leave type not found in organization leave types';
          setError(errorMsg);
          showNotification(errorMsg, 'error');
        }
      }
    } catch (error) {
      const errorMsg = 'Error processing request: ' + (error.message || 'Unknown error');
      setError(errorMsg);
      showNotification(errorMsg, 'error');
      console.error('Error updating leave balance:', error);
    }
  };

  const updateEmployeeLeaveBalance = (leaveTypeId) => {
    const updatedEmployee = { ...currentEmployee };
    const existingBalanceIndex = updatedEmployee.leaveBalances.findIndex(lb =>
      lb.leave_type_id === leaveTypeId
    );
    if (existingBalanceIndex >= 0) {
      updatedEmployee.leaveBalances[existingBalanceIndex] = {
        ...updatedEmployee.leaveBalances[existingBalanceIndex],
        total: currentLeaveBalance.total,
        used: currentLeaveBalance.used,
        pending: currentLeaveBalance.pending,
        remaining: currentLeaveBalance.remaining
      };
    }
    setEmployees(employees.map(emp =>
      emp.id === updatedEmployee.id ? updatedEmployee : emp
    ));
  };

  const handleAddLeaveType = () => {
    if (!currentEmployee.leaveBalances.some(lb => lb.type === currentLeaveType)) {
      const newLeaveBalance = {
        type: currentLeaveType,
        total: currentLeaveBalance.total,
        used: currentLeaveBalance.used,
        pending: currentLeaveBalance.pending,
        remaining: currentLeaveBalance.remaining
      };
      const updatedEmployee = {
        ...currentEmployee,
        leaveBalances: [...currentEmployee.leaveBalances, newLeaveBalance]
      };
      setEmployees(employees.map(emp =>
        emp.id === updatedEmployee.id ? updatedEmployee : emp
      ));
      showNotification('New leave type added successfully', 'success');
      handleCloseDialog();
    }
  };

  const getProgressColor = (remaining, total) => {
    if (total === 0) return theme.palette.grey[500];
    const percentage = (remaining / total) * 100;
    if (percentage <= 20) return theme.palette.error.main;
    if (percentage <= 40) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>

      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
          Leave Balances
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Track and manage employee leave allocations across departments
        </Typography>
      </Box>
      {/* Filters */}
      <Box sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        mb: 3,
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'text.primary' }}>
          Employee Leave Records
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search employee..."
            value={searchTerm}
            onChange={handleSearch}
            sx={{ width: { xs: '100%', sm: 240 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ width: { xs: '100%', sm: 180 } }}>
            <InputLabel>Department</InputLabel>
            <Select
              value={selectedDepartment}
              onChange={handleDepartmentChange}
              label="Department"
            >
              {departments.map(dept => (
                <MenuItem key={dept} value={dept}>{dept}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            size="small"
            sx={{ height: 40 }}
            onClick={() => fetchLeaveBalances(organizationId)}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Box sx={{ mb: 2, p: 2, bgcolor: alpha(theme.palette.error.main, 0.1), borderRadius: 1 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Card elevation={0} sx={{ mb: 2, border: '1px solid #e0e4e8', borderRadius: 1 }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Leave Type</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Total</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Used</TableCell>

                <TableCell sx={{ fontWeight: 600 }}>Remaining</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEmployees
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((employee) => {
                  const selectedBalance = getSelectedLeaveBalance(employee);

                  return (
                    <TableRow
                      key={employee.id}
                      hover
                      sx={{
                        '&:nth-of-type(odd)': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.02),
                        }
                      }}
                    >
                      {/* Employee Info */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                            {employee.avatar}
                          </Avatar>
                          <Box>
                            <Typography sx={{ fontWeight: 500 }}>
                              {employee.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {employee.employeeId}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Department */}
                      <TableCell>
                        {employee.department}
                      </TableCell>

                      {/* Leave Type Dropdown */}
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                          <Select
                            value={selectedLeaveTypes[employee.id] || employee.leaveBalances[0]?.type || ''}
                            onChange={(e) => handleLeaveTypeSelect(employee.id, e.target.value)}
                            displayEmpty
                            size='small'
                            sx={{
                              '& .MuiSelect-select': {
                                py: 1,
                                display: 'flex',
                                alignItems: 'center'
                              }
                            }}
                          >

                            {employee.leaveBalances.map((balance) => (
                              <MenuItem key={balance.type} value={balance.type}>

                                {balance.type}

                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>

                      {/* Total */}
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {selectedBalance.total}
                        </Typography>
                      </TableCell>

                      {/* Used */}
                      <TableCell align="center">
                        <Typography variant="body2">
                          {selectedBalance.used || 0}
                        </Typography>
                      </TableCell>

                      {/* Remaining with Progress */}
                      <TableCell>
                        {selectedBalance.type === "Loss Of Pay" || selectedBalance.type === "Work From Home" ? (
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {selectedBalance.remaining}
                          </Typography>
                        ) : (
                          <Box sx={{ width: '100%', maxWidth: 120 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: selectedBalance.remaining <= 2 ? theme.palette.error.main :
                                    selectedBalance.remaining <= 5 ? theme.palette.warning.main :
                                      theme.palette.success.main,
                                  mr: 1
                                }}
                              >
                                {selectedBalance.remaining ?? 0}
                              </Typography>
                              {selectedBalance.remaining <= 2 && selectedBalance.total > 0 && (
                                <Tooltip title="Low balance">
                                  <WarningIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
                                </Tooltip>
                              )}
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={selectedBalance.total > 0 ? (selectedBalance.remaining / selectedBalance.total) * 100 : 0}
                              sx={{
                                height: 4,
                                borderRadius: 2,
                                backgroundColor: alpha(theme.palette.grey[500], 0.2),
                                '& .MuiLinearProgress-bar': {
                                  backgroundColor: getProgressColor(selectedBalance.remaining, selectedBalance.total),
                                  borderRadius: 2
                                }
                              }}
                            />
                          </Box>
                        )}
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title="Edit Leave Balances">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenDialog(employee)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {/* <Tooltip title="View History">
                            <IconButton
                              size="small"
                              color="secondary"
                            >
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </Tooltip> */}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}

              {/* Empty state */}
              {filteredEmployees.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 4, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <SearchIcon sx={{ fontSize: 48, color: theme.palette.grey[400] }} />
                      <Typography variant="h6" sx={{ color: theme.palette.text.secondary }}>
                        No employees found matching your search.
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
                        Try adjusting your search or filter criteria.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredEmployees.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: '1px solid #e0e4e8',
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
              my: 1
            }
          }}
        />

        <Box sx={{ mb: 4, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {Math.min(rowsPerPage, filteredEmployees.length - page * rowsPerPage)} of {filteredEmployees.length} employees
          </Typography>
        </Box>
      </Card>
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        {currentEmployee && (
          <>
            <DialogTitle sx={{
              borderBottom: '1px solid #e0e4e8',
              pb: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Typography variant="h6">Edit Leave Balance</Typography>
              <IconButton onClick={handleCloseDialog} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 3, px: 3 }}>
              <Box sx={{ display: 'flex', mb: 3, alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 48, height: 48 }}>
                  {currentEmployee.avatar}
                </Avatar>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    {currentEmployee.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {currentEmployee.employeeId} • {currentEmployee.department}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Leave Type</InputLabel>
                    <Select
                      value={currentLeaveType}
                      onChange={handleLeaveTypeChange}
                      label="Leave Type"
                    >
                      {currentEmployee.leaveBalances.map(balance => (
                        <MenuItem key={balance.type} value={balance.type}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: leaveTypeColors[balance.type] || theme.palette.grey[500]
                              }}
                            />
                            {balance.type}
                          </Box>
                        </MenuItem>
                      ))}
                      {organizationLeaveTypes
                        .filter(type => !currentEmployee.leaveBalances.some(balance =>
                          balance.type === type.leave_type))
                        .map(type => (
                          <MenuItem key={type.id} value={type.leave_type}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AddIcon fontSize="small" color="primary" />
                              {type.leave_type}
                            </Box>
                          </MenuItem>
                        ))
                      }
                    </Select>
                  </FormControl>
                </Grid>

                {/* Conditionally render fields based on leave type */}
                {currentLeaveType !== "Loss Of Pay" && currentLeaveType !== "Work From Home" && (
                  <>
                    <Grid item xs={12} md={4}>
                      <TextField
                        name="total"
                        label="Total Allocation"
                        type="number"
                        value={currentLeaveBalance.total}
                        onChange={handleBalanceChange}
                        fullWidth
                        InputProps={{ inputProps: { min: 0, step: 0.5 } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        name="used"
                        label="Used"
                        type="number"
                        value={currentLeaveBalance.used}
                        onChange={handleBalanceChange}
                        fullWidth
                        InputProps={{ inputProps: { min: 0, step: 0.5 } }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="remaining"
                        label="Remaining Balance"
                        type="number"
                        value={currentLeaveBalance.remaining}
                        disabled
                        fullWidth
                        InputProps={{ inputProps: { min: 0 } }}
                        sx={{ bgcolor: alpha(theme.palette.grey[500], 0.05) }}
                      />
                    </Grid>
                  </>
                )}

                {/* Show message for Loss Of Pay/Work From Home */}
                {(currentLeaveType === "Loss Of Pay" || currentLeaveType === "Work From Home") && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      {currentLeaveType} does not require to add leave balance.
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Box sx={{
                mt: 3,
                p: 2,
                bgcolor: alpha(theme.palette.info.main, 0.05),
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
              }}>
                <Typography variant="body2" sx={{ color: theme.palette.info.dark }}>
                  <strong>Note:</strong> Changes to leave balances will be logged in the system audit trail.
                  {currentLeaveType !== "Loss Of Pay" && currentLeaveType !== "Work From Home" && (
                    " The remaining balance is automatically calculated."
                  )}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e4e8' }}>
              <Button
                onClick={handleCloseDialog}
                variant="outlined"
                color="inherit"
                sx={{ mr: 1 }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveBalance}
                variant="contained"
                color="primary"
                disabled={currentLeaveType === "Loss Of Pay" || currentLeaveType === "Work From Home"}
              >
                Save Changes
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};


export default LeaveBalancesWithSnackbar;